/* eslint-disable no-unused-expressions,default-param-last */
import { useState, useLayoutEffect, useTransition, useCallback } from 'react'
import { isArr, PlatformEvent } from 'lib/util'
import type { ListDataItem } from 'ui/Element/List'

export type PaginationResult = ListDataItem[] | {
  data?: ListDataItem[]
  page?: string | null
}

interface Pagination {
  data: ListDataItem[]
  page: string | null
  pending: boolean
  loadNext: () => void
}

interface PaginationState {
  data: ListDataItem[]
  page: string | null
  pending: boolean
}

const initialState: PaginationState = { data: [], page: null, pending: true }

/**
 * usePagination hook
 *
 * Uses `fn` to retrieve data, which is then passed inside the returned object's data property. The
 * data is retrieved in the following cases:
 * · The first time the element is mounted
 * · Whenever the returned `loadNext` is called while there is a next page
 * · Whenever `deps` changes
 *
 * The returned pending property is set to `true` before `fn` is called and to `false` afterward.
 *
 * @param fn        a function to call in order to retrieve data
 * @param startData initial list data
 * @param nextPage  the page to start from
 * @param page      the current page
 * @param setPage   handler for changing the current page
 * @param deps      dependencies array which makes `fn` be called whenever they change
 * @returns         the pagination's state and controls
 */
const usePagination = (
  fn: (e: PlatformEvent<{ page: string | null }>) => Promise<PaginationResult> | PaginationResult,
  startData: ListDataItem[] | null = null,
  nextPage: string | null = null,
  page: string | null = null,
  setPage: (page: string | null) => void,
  deps: unknown[] = [],
): Pagination => {
  const [, startTransition] = useTransition()
  const [state, setState] = useState<PaginationState>(initialState)
  const { data, pending } = state

  const loadNext = useCallback(() => {
    if (!state.page || pending) return
    setPage(state.page)
  }, [state.page, pending])

  useLayoutEffect(() => {
    setState(pending ? state : initialState);
    (async () => {
      try {
        if (startData && !page) {
          setState({
            data: startData,
            page: nextPage,
            pending: false,
          })
        } else {
          startTransition(async () => {
            const result = await fn(new PlatformEvent('open', { page }))
            startTransition(() => {
              const resultData = (isArr(result) ? result : result?.data) || []
              const [first] = resultData
              // TODO: Make search handler return null / [] instead
              // a single item with key -1 marks an empty result
              const noData = (first?.key as unknown) === -1 || !first
              const next = (isArr(result) ? null : result?.page) || null

              setState({
                data: noData ? [] : resultData,
                page: next,
                pending: false,
              })
            })
          })
        }
      } catch (e) {
        setState(pending ? initialState : state)
      }
    })()
  }, [page, ...deps])

  return {
    data,
    page: state.page,
    pending,
    loadNext,
  }
}

export default usePagination
